"use client";

import { useEffect } from "react";
import Link from "next/link";
import * as Sentry from "@sentry/nextjs";

import { HudWindow } from "@/components/ownly/HudWindow";

/**
 * Shown when a page throws. Reports to Sentry so a breakage surfaces here before a
 * visitor has to mention it, and shows plain, non-technical copy: never the error
 * message, the stack, or the digest.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <main
      id="main-content"
      className="ownly-section-divider relative flex min-h-[70vh] w-full min-w-0 flex-col items-center justify-center px-6 pb-24 pt-[calc(5.25rem+3rem)] md:px-8 md:pt-[calc(5.75rem+4rem)] lg:pt-[calc(6rem+4rem)]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.2] ownly-section-grid ownly-section-grid-drift"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_50%_at_50%_35%,rgba(43,127,255,0.1),transparent_60%)]"
        aria-hidden
      />
      <HudWindow title="ownly · status" className="relative z-10 mx-auto max-w-lg">
        <div className="text-center">
          <p className="ownly-eyebrow">Something went wrong</p>
          <h1 className="ownly-display mt-4 text-white">
            This page didn&apos;t load properly
          </h1>
          <p className="ownly-body mt-4 ownly-prose leading-[27px] text-white/85">
            Our team has been notified automatically. Try again in a moment, and if
            it keeps happening, email us at team@ownlydigital.co.uk and we&apos;ll
            sort it.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={reset}
              className="ownly-control ownly-btn-primary inline-flex items-center justify-center rounded-full bg-ownly-accent px-6 py-3 text-white shadow-[var(--ownly-cta-shadow)] transition-[box-shadow,transform,background-color] duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#3d8aff] hover:shadow-[var(--ownly-cta-shadow-hover)] active:translate-y-0 active:scale-[0.98]"
            >
              Try again
            </button>
            <Link
              href="/"
              className="ownly-control inline-flex items-center justify-center rounded-full border border-white/[0.14] bg-white/[0.05] px-6 py-3 text-white/90 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/[0.22] hover:bg-white/[0.09]"
            >
              Back to home
            </Link>
          </div>
        </div>
      </HudWindow>
    </main>
  );
}
